import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, AlertCircle, RefreshCw, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SystemStatus {
  service: string;
  status: "connected" | "error" | "warning";
  message: string;
  canRetry: boolean;
  canViewLogs: boolean;
}

export const SystemHealthWidget = () => {
  const [systems, setSystems] = useState<SystemStatus[]>([
    { service: "Gmail Sync", status: "connected", message: "Connected", canRetry: true, canViewLogs: false },
    { service: "Call Monitor", status: "connected", message: "Operational", canRetry: false, canViewLogs: true }
  ]);

  const checkGmailStatus = async () => {
    // Simplified status check - can be enhanced later
    setSystems(prev => prev.map(s => 
      s.service === "Gmail Sync" 
        ? { ...s, status: "connected", message: "Connected" }
        : s
    ));
  };

  useEffect(() => {
    checkGmailStatus();
    const interval = setInterval(checkGmailStatus, 30000); // Check every 30s
    return () => clearInterval(interval);
  }, []);

  const handleRetry = (service: string) => {
    if (service === "Gmail Sync") {
      window.open('/settings?tab=integrations', '_blank');
    }
  };

  const getStatusIcon = (status: SystemStatus["status"]) => {
    switch (status) {
      case "connected":
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case "error":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "warning":
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: SystemStatus["status"]) => {
    switch (status) {
      case "connected":
        return "bg-green-500/10 text-green-600 border-green-500/30";
      case "error":
        return "bg-red-500/10 text-red-600 border-red-500/30";
      case "warning":
        return "bg-yellow-500/10 text-yellow-600 border-yellow-500/30";
    }
  };

  return (
    <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/30 rounded-3xl shadow-xl">
      <CardContent className="p-4">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <span className="text-blue-600">⚡</span>
          System Health
        </h3>
        
        <div className="space-y-2">
          {systems.map((system) => (
            <div key={system.service} className="flex items-center justify-between p-2 bg-background/50 rounded-lg">
              <div className="flex items-center gap-2 flex-1">
                {getStatusIcon(system.status)}
                <div className="flex-1">
                  <p className="text-xs font-medium">{system.service}</p>
                  <Badge className={`text-[10px] px-1.5 py-0 ${getStatusColor(system.status)}`}>
                    {system.message}
                  </Badge>
                </div>
              </div>
              
              <div className="flex gap-1">
                {system.canRetry && system.status === "error" && (
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-7 w-7 p-0"
                    onClick={() => handleRetry(system.service)}
                  >
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                )}
                {system.canViewLogs && (
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-7 w-7 p-0"
                    onClick={() => toast.info("View logs feature coming soon")}
                  >
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
